'use client';

import { useEffect, useRef, useState } from 'react';
import { Check, ChevronRight, Search, UserPlus, X } from 'lucide-react';
import { useResponsiveSheet } from '@church/nextjs-ui/components/ResponsiveSheet';
import type {
  OutsideRelative,
  Person,
  Relationship,
  RelationshipRequest,
  SearchHit,
} from '@/lib/types';
import { fmtShortDate } from '@/lib/format';
import { useProfile } from './ProfileContext';
import { Avatar } from './Avatar';
import { groupByRelationshipType } from './grouping';
import { formerElsewhere, household, relationOf } from './relations';
import { relationshipLabel } from './RelationshipPage';
import { TextArea } from './forms';
import { CARD, CTA_SMALL, CTA_TEAL, CardsSkeleton, Chip, FIELD, Kicker, PageBody } from './ui';

/**
 * The Family page of the member sheet, for any person in the household:
 * everyone else in the house with what they are to this person (a row
 * without one says "Not set" and opens the picker), then relatives who
 * live elsewhere, grouped by type, family rows removable. Requests waiting
 * on this person that you can answer (a head of household answers for
 * anyone in the house). On your own page: requests you sent, and "Add someone
 * elsewhere" (a search, a relationship and a note; they confirm before MP
 * learns anything).
 */

/** Drafts shared between sibling pages of the sheet (module-level, like the spouse flow's). */
let relateDraft: Person | null = null;
let findDraft: SearchHit | null = null;
let pickDraft: Relationship | null = null;
/** The relative elsewhere whose relationship the change page is changing, and whose row it is. */
let changeDraft: { subject: Person; rel: OutsideRelative } | null = null;
export function setRelateDraft(p: Person | null) {
  relateDraft = p;
}
export function getRelateDraft() {
  return relateDraft;
}

export function FamilyPage({ person }: { person: Person }) {
  const { api, data, run, update, requests, setRequests, patchRequests } = useProfile();
  const { navigate } = useResponsiveSheet();
  const isMe = person.contactId === data.me.contactId;
  const first = person.nickname || person.firstName;
  const others = household(data).filter((p) => p.contactId !== person.contactId);
  // Requests I can answer for this person: mine, or, as a head of household,
  // anyone's in the house (the API only sends the ones I may answer).
  const incoming =
    requests && requests !== 'error'
      ? requests.incoming.filter((r) => r.toContactId === person.contactId)
      : [];
  const outgoing = isMe && requests && requests !== 'error' ? requests.outgoing : [];
  const groups = groupByRelationshipType(person.family, (rel) => rel, data.lookups);
  // Who used to live here with no relationship to this person; after the groups.
  const formerly = formerElsewhere(data, person);

  const answer = (req: RelationshipRequest, action: 'accept' | 'decline' | 'cancel') => {
    const before = requests;
    const snapshot = data;
    patchRequests((r) => ({
      incoming: r.incoming.filter((x) => x.id !== req.id),
      outgoing: r.outgoing.filter((x) => x.id !== req.id),
    }));
    if (action === 'accept') {
      // Their side of the request is what lands on the asked person's family
      // list (mine, or a household member's when a head answers for them):
      // the reciprocal of what the asker said that person is to them.
      const all = data.lookups.relationshipTypes.flatMap((t) =>
        t.relationships.map((r) => ({ r, t }))
      );
      const theirs = all.find((x) => x.r.id === req.relationshipId);
      const mine = all.find((x) => x.r.id === theirs?.r.reciprocalId) ?? theirs;
      const add = (p: Person): Person => ({
        ...p,
        family: [
          ...p.family,
          {
            contactRelationshipId: -Date.now(),
            relationshipId: mine?.r.id ?? req.relationshipId,
            label: mine ? mine.r.name : 'Family',
            typeId: mine?.t.id ?? null,
            typeName: mine?.t.name ?? null,
            editable: true,
            contactId: req.from.contactId,
            displayName: req.from.displayName,
            imageUrl: req.from.imageUrl,
            householdId: null,
          },
        ],
      });
      update((d) => ({
        ...d,
        me: d.me.contactId === req.toContactId ? { ...d.me, ...add(d.me) } : d.me,
        members: d.members.map((m) => (m.contactId === req.toContactId ? add(m) : m)),
      }));
    }
    void run(
      () => api.answerRequest(req.id, action),
      () => {
        setRequests(before);
        update(() => snapshot);
      },
      action === 'accept' ? `${req.from.displayName} is now family` : undefined
    );
  };

  const remove = (rel: OutsideRelative) => {
    const snapshot = data;
    const drop = (p: Person): Person => ({
      ...p,
      family: p.family.filter((f) => f.contactRelationshipId !== rel.contactRelationshipId),
    });
    update((d) => ({
      ...d,
      me: d.me.contactId === person.contactId ? { ...d.me, ...drop(d.me) } : d.me,
      members: d.members.map((m) => (m.contactId === person.contactId ? drop(m) : m)),
    }));
    void run(
      () => api.removeRelationship(rel.contactRelationshipId),
      () => update(() => snapshot)
    );
  };

  const relate = (other: Person) => {
    relateDraft = other;
    navigate('relate');
  };

  const change = (rel: OutsideRelative) => {
    changeDraft = { subject: person, rel };
    navigate('rechange');
  };

  return (
    <PageBody>
      {others.length > 0 && (
        <div className="flex flex-col gap-2">
          <Kicker>In the household</Kicker>
          <div className={`${CARD} divide-y divide-gray-100 overflow-hidden`}>
            {others.map((o) => {
              const rel = relationOf(person, o.contactId);
              return (
                <button
                  key={o.contactId}
                  type="button"
                  onClick={() => relate(o)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 active:bg-gray-100"
                >
                  <Avatar name={o.displayName} imageUrl={o.imageUrl} size={40} />
                  <span className="min-w-0 flex-1">
                    <span className="pii block truncate text-sm font-semibold text-[#1c2b39]">
                      {o.contactId === data.me.contactId ? 'You' : o.displayName}
                    </span>
                    <span
                      className={`pii block truncate text-xs ${rel ? 'text-gray-500' : 'font-semibold text-[#299c8f]'}`}
                    >
                      {rel
                        ? `${isMe ? 'Your' : `${first}'s`} ${rel.label.toLowerCase()}`
                        : `How is ${o.contactId === data.me.contactId ? 'this' : o.nickname || o.firstName} related to ${isMe ? 'you' : first}?`}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" strokeWidth={2.5} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {incoming.length > 0 && (
        <div className="flex flex-col gap-2">
          <Kicker>{isMe ? 'Waiting on you' : 'Waiting on an answer'}</Kicker>
          {incoming.map((req) => (
            <div key={req.id} className={`${CARD} flex flex-col gap-3 p-4 ring-2 ring-[#299c8f]`}>
              <div className="flex items-center gap-3">
                <Avatar name={req.from.displayName} imageUrl={req.from.imageUrl} size={44} />
                <span className="min-w-0 flex-1">
                  <span className="pii block text-sm font-semibold text-[#1c2b39]">
                    {req.from.displayName} says {isMe ? "you're" : `${first} is`} their{' '}
                    {req.label.replace(/\s+of$/i, '').toLowerCase()}
                  </span>
                  <span className="block text-xs text-gray-500">
                    Sent {fmtShortDate(req.createdAt)}
                  </span>
                </span>
              </div>
              {req.note && <p className="text-sm text-gray-600">“{req.note}”</p>}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => answer(req, 'accept')}
                  className={`${CTA_SMALL} bg-[#299c8f] text-white`}
                >
                  <Check className="h-4 w-4" strokeWidth={3} />
                  Accept
                </button>
                <button
                  type="button"
                  onClick={() => answer(req, 'decline')}
                  className={`${CTA_SMALL} bg-white text-[#1c2b39] ring-1 ring-gray-200`}
                >
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(groups.length > 0 || formerly.length > 0 || outgoing.length > 0 || isMe) && (
        <div className="flex flex-col gap-2">
          <Kicker>Elsewhere</Kicker>
          {groups.length === 0 && formerly.length === 0 && outgoing.length === 0 && (
            <p className="text-sm text-gray-500">
              Grandparents, grown kids, in-laws: link family who live elsewhere so you can sign each
              other's kids up for camp and see each other at the campus.
            </p>
          )}
          {groups.map((group) => (
            <div key={group.key} className="flex flex-col gap-1.5">
              {groups.length > 1 && (
                <span className="px-1 text-[11px] font-bold tracking-widest text-gray-400 uppercase">
                  {group.label}
                </span>
              )}
              <div className={`${CARD} divide-y divide-gray-100 overflow-hidden`}>
                {group.items.map((rel) => (
                  <div
                    key={rel.contactRelationshipId}
                    className="flex items-center gap-3 px-4 py-3"
                  >
                    {/* A family row is tappable to change what they are, like a household
                        row; business and care rows are the office's. */}
                    <button
                      type="button"
                      disabled={!rel.editable}
                      onClick={() => change(rel)}
                      className="-my-1 -ml-1 flex min-w-0 flex-1 items-center gap-3 rounded-xl py-1 pl-1 text-left transition-colors enabled:hover:bg-gray-50 enabled:active:bg-gray-100"
                    >
                      <Avatar name={rel.displayName} imageUrl={rel.imageUrl} size={40} />
                      <span className="min-w-0 flex-1">
                        <span className="pii block truncate text-sm font-semibold text-[#1c2b39]">
                          {rel.displayName}
                        </span>
                        <span className="block truncate text-xs text-gray-500">{rel.label}</span>
                      </span>
                      {rel.editable && (
                        <ChevronRight
                          className="h-4 w-4 shrink-0 text-gray-400"
                          strokeWidth={2.5}
                        />
                      )}
                    </button>
                    {rel.editable && (
                      <button
                        type="button"
                        onClick={() => remove(rel)}
                        aria-label={`Remove ${rel.displayName}`}
                        className="shrink-0 rounded-full p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                      >
                        <X className="h-4 w-4" strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          {formerly.length > 0 && (
            <div className="flex flex-col gap-1.5">
              {groups.length > 0 && (
                <span className="px-1 text-[11px] font-bold tracking-widest text-gray-400 uppercase">
                  Lived here
                </span>
              )}
              <div className={`${CARD} divide-y divide-gray-100 overflow-hidden`}>
                {formerly.map((f) => (
                  <div key={f.contactId} className="flex items-center gap-3 px-4 py-3">
                    <Avatar name={f.name} imageUrl={f.imageUrl} size={40} />
                    <span className="min-w-0 flex-1">
                      <span className="pii block truncate text-sm font-semibold text-[#1c2b39]">
                        {f.name}
                      </span>
                      <span className="block truncate text-xs text-gray-500">
                        {[
                          f.position,
                          `lived here until ${fmtShortDate(f.leftOn)}`,
                          f.nowHouseholdName
                            ? `now with the ${f.nowHouseholdName} household`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {outgoing.length > 0 && (
            <div className={`${CARD} divide-y divide-gray-100 overflow-hidden`}>
              {outgoing.map((req) => (
                <div key={req.id} className="flex items-center gap-3 px-4 py-3">
                  <Avatar name={req.to.displayName} imageUrl={req.to.imageUrl} size={40} />
                  <span className="min-w-0 flex-1">
                    <span className="pii block truncate text-sm font-semibold text-[#1c2b39]">
                      {req.to.displayName}
                    </span>
                    <span className="block truncate text-xs text-gray-500">
                      {req.label} · waiting on them · sent {fmtShortDate(req.createdAt)}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => answer(req, 'cancel')}
                    className="shrink-0 text-xs font-semibold text-gray-400 hover:text-red-600"
                  >
                    Cancel
                  </button>
                </div>
              ))}
            </div>
          )}
          {isMe ? (
            <button
              type="button"
              onClick={() => navigate('find')}
              className={`${CTA_TEAL} self-start`}
            >
              <UserPlus className="h-4 w-4" strokeWidth={2.5} />
              Add someone elsewhere
            </button>
          ) : (
            <p className="text-xs text-gray-500">
              Linking {first} to someone outside the household goes through that person's own
              profile, or the campus office.
            </p>
          )}
        </div>
      )}
      <p className="text-xs text-gray-500">
        Business and care relationships are kept by the office; ask them to change those.
      </p>
    </PageBody>
  );
}

/** Relationship types a person may set themselves: the family ones (the API's isFamilyType). */
const FAMILY_TYPE = /family|in-law|step|foster/i;

/**
 * Change what a relative who lives elsewhere is to this person: the family
 * relationships as chips, the current one marked. One tap saves straight
 * away (both MP rows; no new request) and goes back to the Family page.
 */
export function ChangeRelationshipPage() {
  const { api, data, run, update } = useProfile();
  const { goBack } = useResponsiveSheet();
  const draft = changeDraft;
  if (!draft) return null;
  const { subject, rel } = draft;
  const isMe = subject.contactId === data.me.contactId;
  const whose = isMe ? 'your' : `${subject.nickname || subject.firstName}'s`;

  // Swap one family row on the subject, wherever the subject sits in the data.
  const patchRow = (rowId: number, next: Partial<OutsideRelative>) =>
    update((d) => {
      const fix = <P extends Person>(p: P): P =>
        p.contactId !== subject.contactId
          ? p
          : {
              ...p,
              family: p.family.map((f) =>
                f.contactRelationshipId === rowId ? { ...f, ...next } : f
              ),
            };
      return { ...d, me: fix(d.me), members: d.members.map(fix) };
    });

  const pick = (r: Relationship, typeId: number, typeName: string) => {
    goBack();
    if (r.id === rel.relationshipId) return;
    const snapshot = data;
    patchRow(rel.contactRelationshipId, {
      relationshipId: r.id,
      label: relationshipLabel(r, null),
      typeId,
      typeName,
    });
    void run(
      async () => {
        // The new row has a new id (and MP's gendered label); keyed on it, a
        // second change finds the row.
        const saved = await api.changeRelationship(rel.contactRelationshipId, r.id);
        patchRow(rel.contactRelationshipId, saved);
      },
      () => update(() => snapshot),
      `${rel.displayName} is now ${whose} ${relationshipLabel(r, null).toLowerCase()}`
    );
  };

  return (
    <PageBody>
      <div className="flex items-center gap-3">
        <Avatar name={rel.displayName} imageUrl={rel.imageUrl} size={40} />
        <p className="text-sm text-gray-600">
          <span className="pii font-semibold text-[#1c2b39]">{rel.displayName}</span> is {whose}…
        </p>
      </div>
      {[...data.lookups.relationshipTypes]
        .filter((t) => FAMILY_TYPE.test(t.name))
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((t) => (
          <div key={t.id} className="flex flex-col gap-2">
            <Kicker>{t.name}</Kicker>
            <div className="flex flex-wrap gap-2">
              {t.relationships.map((r) => (
                <Chip
                  key={r.id}
                  active={r.id === rel.relationshipId}
                  onClick={() => pick(r, t.id, t.name)}
                >
                  {relationshipLabel(r, null)}
                </Chip>
              ))}
            </div>
          </div>
        ))}
      <p className="text-xs text-gray-500">
        Changes straight away, for both of you. To unlink them instead, use the × on the Family
        page.
      </p>
    </PageBody>
  );
}

/** The "Add someone elsewhere" flow's pages, on the member sheet (me only). */
export function FindPage() {
  return (
    <SearchPage
      onPick={(p) => {
        findDraft = p;
      }}
      nextPage="pick"
    />
  );
}
export function PickPage() {
  if (!findDraft) return null;
  return (
    <PickRelationshipPage
      person={findDraft}
      onPick={(r) => {
        pickDraft = r;
      }}
      nextPage="ask"
    />
  );
}
export function AskPage({ onDone }: { onDone: () => void }) {
  if (!findDraft || !pickDraft) return null;
  return <NotePage person={findDraft} relationship={pickDraft} onClose={onDone} />;
}

export function SearchPage({
  onPick,
  nextPage = 'relationship',
  intro,
}: {
  onPick: (p: SearchHit) => void;
  /** Where a pick goes next */
  nextPage?: string;
  intro?: string;
}) {
  const { api } = useProfile();
  const { navigate } = useResponsiveSheet();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (timer.current) window.clearTimeout(timer.current);
    const term = q.trim();
    if (term.length < 3) {
      setHits(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const controller = new AbortController();
    timer.current = window.setTimeout(() => {
      api
        .searchPeople(term, controller.signal)
        .then((h) => {
          if (!controller.signal.aborted) setHits(h);
        })
        .catch(() => {
          if (!controller.signal.aborted) setHits([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setSearching(false);
        });
    }, 300);
    return () => {
      controller.abort();
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [q, api]);

  return (
    <PageBody>
      {intro && <p className="text-sm text-gray-600">{intro}</p>}
      <label className="relative block">
        <Search
          className="pointer-events-none absolute top-1/2 left-4 h-4 w-4 -translate-y-1/2 text-gray-400"
          strokeWidth={2.5}
        />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name"
          className={`${FIELD} pl-11`}
          maxLength={80}
          autoComplete="off"
        />
      </label>
      {q.trim().length < 3 ? (
        <p className="text-xs text-gray-500">
          Type at least three letters. Only people who have opted into the directory show up.
        </p>
      ) : searching && hits === null ? (
        <CardsSkeleton count={2} />
      ) : hits && hits.length === 0 ? (
        <p className="text-sm text-gray-500">
          No one by that name. They may not be in the directory yet; the campus office can help.
        </p>
      ) : hits ? (
        <div
          className={`${CARD} divide-y divide-gray-100 overflow-hidden ${searching ? 'opacity-60' : ''}`}
        >
          {hits.map((p) => (
            <button
              key={p.contactId}
              type="button"
              onClick={() => {
                onPick(p);
                navigate(nextPage);
              }}
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 active:bg-gray-100"
            >
              <Avatar name={p.displayName} imageUrl={p.imageUrl} size={36} />
              <span className="min-w-0 flex-1">
                <span className="pii block truncate text-sm font-semibold text-[#1c2b39]">
                  {p.displayName}
                </span>
                {p.campus && (
                  <span className="block truncate text-xs text-gray-500">{p.campus}</span>
                )}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" strokeWidth={2.5} />
            </button>
          ))}
        </div>
      ) : null}
    </PageBody>
  );
}

export function PickRelationshipPage({
  person,
  onPick,
  nextPage = 'note',
}: {
  person: SearchHit;
  onPick: (r: Relationship) => void;
  nextPage?: string;
}) {
  const { data } = useProfile();
  const { navigate } = useResponsiveSheet();
  return (
    <PageBody>
      <div className="flex items-center gap-3">
        <Avatar name={person.displayName} imageUrl={person.imageUrl} size={40} />
        <p className="text-sm text-gray-600">
          <span className="pii font-semibold text-[#1c2b39]">{person.displayName}</span> is your…
        </p>
      </div>
      {[...data.lookups.relationshipTypes]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((t) => (
          <div key={t.id} className="flex flex-col gap-2">
            <Kicker>{t.name}</Kicker>
            <div className="flex flex-wrap gap-2">
              {t.relationships.map((r) => (
                <Chip
                  key={r.id}
                  active={false}
                  onClick={() => {
                    onPick(r);
                    navigate(nextPage);
                  }}
                >
                  {relationshipLabel(r, null)}
                </Chip>
              ))}
            </div>
          </div>
        ))}
    </PageBody>
  );
}

export function NotePage({
  person,
  relationship,
  onClose,
}: {
  person: SearchHit;
  relationship: Relationship;
  onClose: () => void;
}) {
  const { api, data, run, patchRequests } = useProfile();
  const onSent = (req: RelationshipRequest) =>
    patchRequests((r) => ({ ...r, outgoing: [...r.outgoing, req] }));
  const onReplace = (tempId: number, req: RelationshipRequest) =>
    patchRequests((r) => ({ ...r, outgoing: r.outgoing.map((x) => (x.id === tempId ? req : x)) }));
  const onFailed = (tempId: number) =>
    patchRequests((r) => ({ ...r, outgoing: r.outgoing.filter((x) => x.id !== tempId) }));
  const [note, setNote] = useState('');
  // The request says what THEY are to me; the API stores it as what the
  // other person is to the requester.
  const label = relationshipLabel(relationship, null);

  const send = () => {
    const tempId = -Date.now();
    const temp: RelationshipRequest = {
      id: tempId,
      fromContactId: data.me.contactId,
      toContactId: person.contactId,
      from: {
        contactId: data.me.contactId,
        displayName: data.me.displayName,
        imageUrl: data.me.imageUrl,
      },
      to: {
        contactId: person.contactId,
        displayName: person.displayName,
        imageUrl: person.imageUrl,
      },
      relationshipId: relationship.id,
      label,
      note: note.trim() || null,
      status: 'pending',
      createdAt: new Date().toISOString().slice(0, 19),
      respondedAt: null,
    };
    onSent(temp);
    onClose();
    void run(
      async () => {
        const saved = await api.sendRequest({
          toContactId: person.contactId,
          relationshipId: relationship.id,
          note: note.trim() || undefined,
        });
        onReplace(tempId, saved);
      },
      () => onFailed(tempId),
      `Request sent to ${person.displayName}`
    );
  };

  return (
    <PageBody>
      <div className={`${CARD} flex items-center gap-3 p-4`}>
        <Avatar name={person.displayName} imageUrl={person.imageUrl} size={40} />
        <span className="min-w-0 flex-1">
          <span className="pii block truncate text-sm font-semibold text-[#1c2b39]">
            {person.displayName}
          </span>
          <span className="block truncate text-xs text-gray-500">Your {label.toLowerCase()}</span>
        </span>
      </div>
      <TextArea
        label="A note for them"
        value={note}
        onChange={setNote}
        placeholder="So they know it's you."
        help="Optional"
      />
      <button type="button" onClick={send} className={`${CTA_TEAL} self-start`}>
        Send request
        <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
      </button>
    </PageBody>
  );
}
